// React integration
import React from 'react'
import { createRoot } from 'react-dom/client'

// Make React available globally
window.React = React
window.ReactDOM = { createRoot }
import ControlPanel from './components/ControlPanel'
import InventoryManager from './components/InventoryManager'
import SimpleInventoryManager from './components/SimpleInventoryManager'
import CountSessionManager from './components/CountSessionManager'
import CountInterface from './components/CountInterface'
import CountSession from './components/CountSession'
import SessionsCalendar from './components/SessionsCalendar'
import CountSessionItemEditor from './components/CountSessionItemEditor'
import ItemLocationFolders from './components/ItemLocationFolders'
import CounterIntelligenceDrawer from './components/CounterIntelligenceDrawer'
import GroupEditor from './components/GroupEditor'
import VendorEditor from './components/VendorEditor'
import TaskAlertDashboard from './components/TaskAlertDashboard'
import AICommandCenter from './components/AICommandCenter'
import ItemEditor from './components/ItemEditor'
import ItemDatabaseBrowser from './components/ItemDatabaseBrowser'
import InventoryModeToggle from './components/InventoryModeToggle'
// import CountTemplateManager from './components/CountTemplateManager'
// import CountSessionInterface from './components/CountSessionInterface'
// import CountResultsViewer from './components/CountResultsViewer'
import Reports from './components/Reports'
import OrdersPage from './components/OrdersPage'
import OrderingHub from './components/ordering/OrderingHub'
import SessionArchivePage from './components/SessionArchivePage'
import SessionTrashPage from './components/SessionTrashPage'
import UpdatePrices from './components/UpdatePrices'
import InvoiceInbox from './components/InvoiceInbox'
import PNLCategoriesSettings from './components/PNLCategoriesSettings'

import ImportApprovalTable from './components/ImportApprovalTable'
import VendorCaptureCard from './components/VendorCaptureCard'
import FeeAssumptionsCard from './components/FeeAssumptionsCard'
import { fetchOptionsForItems } from './services/purchaseOptionService'
import { activeOptions, distinctVendorCount } from './utils/itemCostSummary'
import { isDeclinedImportRow } from './utils/importLanding'
import CycleSelector, { cycleToBitmask, bitmaskToCycle, cycleToFriendlyLabel, getNextOccurrence, inventoryPeriodConfigToCycle } from './components/CycleSelector'
import './utils/dashPrepActiveSignal'

// Import Item Card Library
import ItemCard from './components/cards/ItemCard'
import ItemCardContainer from './components/cards/ItemCardContainer'
import ItemCardDemo from './components/cards/ItemCardDemo'
import DatabaseBrowser from './components/cards/DatabaseBrowser'
import DatabaseColumn from './components/cards/DatabaseColumn'
import { mountHelpInfoSlots } from './components/HelpTooltip'
// import ModernLayout from './components/ModernLayout'
// import App from './components/App'
import axios from 'axios'

// Make components available globally for HTML pages
window.ControlPanel = ControlPanel
window.InventoryManager = InventoryManager
window.SimpleInventoryManager = SimpleInventoryManager
window.CountSessionManager = CountSessionManager
window.CountInterface = CountInterface
window.CountSession = CountSession
window.CountSessionItemEditor = CountSessionItemEditor
window.ItemLocationFolders = ItemLocationFolders
window.GroupEditor = GroupEditor
window.CountTemplateCreator = GroupEditor
window.ItemEditor = ItemEditor
window.ItemDatabaseBrowser = ItemDatabaseBrowser
window.InventoryModeToggle = InventoryModeToggle
window.SessionsCalendar = SessionsCalendar
// Mounts "ⓘ" help-tooltip triggers into declarative data-help-topic slots on
// plain-HTML pages (e.g. the items.html add-items cards).
window.mountHelpInfoSlots = (rootEl) => mountHelpInfoSlots(rootEl, React, window.ReactDOM.createRoot)
window.TaskAlertDashboard = TaskAlertDashboard
window.AICommandCenter = AICommandCenter
window.Reports = Reports
window.OrdersPage = OrdersPage
window.OrderingHub = OrderingHub
window.VendorEditor = VendorEditor
window.SessionArchivePage = SessionArchivePage
window.SessionTrashPage = SessionTrashPage
window.UpdatePrices = UpdatePrices
window.InvoiceInbox = InvoiceInbox
window.PNLCategoriesSettings = PNLCategoriesSettings

window.ImportApprovalTable = ImportApprovalTable
window.CycleSelector = CycleSelector
window.cycleToBitmask = cycleToBitmask
window.bitmaskToCycle = bitmaskToCycle
window.cycleToFriendlyLabel = cycleToFriendlyLabel
window.getNextOccurrence = getNextOccurrence
window.inventoryPeriodConfigToCycle = inventoryPeriodConfigToCycle

// Mount menu-starter approval overlay using the shared ImportApprovalTable React component.
// Returns true on success, false if React is unavailable.
window.mountMenuStarterApproval = function(el, initialItems, onApprove, onBack) {
  if (!el || !window.React || !window.ReactDOM) return false;
  // Tag every item as 'new' so ImportApprovalTable shows them in the New Items
  // section. Items coming from a menu scan have no existing catalog match and no
  // pre-existing _classification field, which would cause them to be invisible
  // (effectiveClassification returns null → filtered into no section).
  const taggedItems = (initialItems || []).map(item => ({
    ...item,
    _classification: item._classification || 'new',
  }));
  function MenuStarterApprovalMount({ initItems }) {
    const [items, setItems] = React.useState(initItems);
    const [isImporting, setIsImporting] = React.useState(false);
    const handleUpdateItem = React.useCallback((id, field, value) => {
      setItems(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
    }, []);
    // Skipped-as-unchanged and explicitly disapproved both mean "don't save
    // this" — the host deletes whatever lands in `rejected`.
    const handleImport = React.useCallback(async () => {
      setIsImporting(true);
      const approved = items.filter((i) => !isDeclinedImportRow(i));
      const rejected = items.filter(isDeclinedImportRow);
      try { await onApprove(approved, rejected); } finally { setIsImporting(false); }
    }, [items]);
    return React.createElement(ImportApprovalTable, {
      items,
      onUpdateItem: handleUpdateItem,
      onReclassifyItem: React.useCallback((id, cls) => {
        setItems(prev => prev.map(item => item.id === id ? { ...item, _classificationOverride: cls } : item));
      }, []),
      onImport: handleImport,
      onBack,
      isImporting,
      hidePriceColumn: true,
    });
  }
  const root = window.ReactDOM.createRoot(el);
  root.render(React.createElement(MenuStarterApprovalMount, { initItems: taggedItems }));
  el._importApprovalRoot = root;
  return true;
};

// Mount the shared P&L category editor — the SAME inventory-category → P&L
// assignment table used by the End-of-Period report step — onto a static page
// such as store-settings.html. Reuses PNLCategoriesSettings so the editor,
// add/rename/delete, persistence, and data model (pnl_categories +
// pnl_category_mappings, store-scoped) are identical to the in-app Settings
// surface. The component resolves the active store from
// localStorage('selectedStoreId') and uses the shared authenticated Supabase
// client, so it shows and edits the same global mappings the wizard wrote.
// Returns true on success, false if React is unavailable.
window.mountPnlCategoriesSettings = function(el, opts) {
  if (!el || !window.React || !window.ReactDOM) return false;
  if (el._pnlCategoriesRoot) return true; // idempotent — one mount per element
  const options = opts || {};
  const root = window.ReactDOM.createRoot(el);
  root.render(React.createElement(PNLCategoriesSettings, {
    onDone: typeof options.onDone === 'function' ? options.onDone : undefined,
  }));
  el._pnlCategoriesRoot = root;
  return true;
};

// Generic mount used by the drag-and-drop document import flow (and any other
// caller that wants the shared ImportApprovalTable inside the menu-starter
// approval overlay). Behaves like mountMenuStarterApproval but with prices
// visible by default and a small `_getCurrentItems()` getter on the mount
// element so the host page can capture the live edited/reclassified items
// when persisting an in-progress snapshot.
window.mountImportApproval = function(el, opts) {
  if (!el || !window.React || !window.ReactDOM) return false;
  const {
    items: initialItems = [],
    onApprove,
    onCancel,
    hidePriceColumn = false,
    importButtonLabel,
    storeId,
    columns,
    rowNoun = 'item',
    allowVendorProduct = false,
    // Opt-in help-page id (utils/helpTopics.js) — enables the ⓘ help triggers
    // on the review-workflow concepts for the hosting flow.
    helpPage,
    // Optional vendor-contact captures found on the imported document(s),
    // shown as a VendorCaptureCard in the lower portion of the review modal.
    // Each: { key, supplierName, sourceFile, contact, resolution, vendors, choice }.
    // Usually arrives AFTER mount (analysis runs in the background) via
    // el._setVendorCaptures(captures).
    vendorCaptures: initialVendorCaptures = [],
    // Optional additional-charge captures (invoice fees the reader itemized),
    // shown as a FeeAssumptionsCard beneath the VendorCaptureCard.
    // Each: { key, supplierName, sourceFile, charges, plan, choices }.
    // Usually arrives AFTER mount via el._setFeeCaptures(captures).
    feeCaptures: initialFeeCaptures = [],
    // Optional relationship groups the import found (relatedReviewGroups
    // output from ImportConsolidationReview). Rendered as the collapsed
    // "Relationships Countertop found" section under the product table; the
    // reviewer's keep/decline decisions come back on approve as
    // extras.relationshipDecisions for the host to feed into planChanges.
    relationships: initialRelationshipGroups = [],
    // What the import session found, rendered as the summary above the review
    // table. See ImportApprovalTable's `importSummary` prop. Absent for the
    // flows that reuse this table outside a document import.
    importSummary: initialImportSummary = null,
  } = opts || {};
  const taggedItems = (initialItems || []).map((item) => ({
    ...item,
    _classification: item._classification || 'new',
  }));
  const seedCaptureChoice = (capture) => ({
    ...capture,
    choice: capture.choice || (
      capture.resolution && capture.resolution.status === 'matched' && capture.resolution.vendorId
        ? { mode: 'existing', vendorId: String(capture.resolution.vendorId) }
        : { mode: 'new' }
    ),
  });
  // Seed the per-fee accept checkboxes: every plan entry defaults to checked
  // unless a choice was already recorded (controlled state round-trips).
  const seedFeeChoices = (capture) => {
    const plan = Array.isArray(capture.plan) ? capture.plan : [];
    const choices = { ...(capture.choices || {}) };
    plan.forEach((p) => {
      if (p && p.column && !(p.column in choices)) choices[p.column] = true;
    });
    return { ...capture, choices };
  };
  let liveItems = taggedItems;
  // The modal's live relationship decisions (unchecked = declined). Captured
  // outside React so handleImport can include the latest set in extras even
  // though the section manages its own checkbox state.
  let liveRelationshipDecisions = null;
  let liveVendorCaptures = (initialVendorCaptures || []).map(seedCaptureChoice);
  let setVendorCapturesRef = null;
  let pendingVendorCaptures = null; // arrived before the React setter registered
  let liveFeeCaptures = (initialFeeCaptures || []).map(seedFeeChoices);
  let setFeeCapturesRef = null;
  let pendingFeeCaptures = null; // arrived before the React setter registered
  // Live-append channel: the setter handle a host page uses to push newly-added
  // rows into an already-mounted table WITHOUT a remount (see el._setItems /
  // el._appendItems below). Used by the Add Items page's unified live list, where
  // every add method (import, manual, sample, menu, chat) appends into one table.
  let setItemsRef = null;
  let pendingSetItems = null; // set() called before the React setter registered
  const tagNew = (item) => ({ ...item, _classification: item._classification || 'new' });
  function ImportApprovalMount({ initItems, initCaptures, initFeeCaptures, initConfig }) {
    const [items, setItems] = React.useState(initItems);
    const [isImporting, setIsImporting] = React.useState(false);
    const [vendorCaptures, setVendorCaptures] = React.useState(initCaptures);
    const [feeCaptures, setFeeCaptures] = React.useState(initFeeCaptures);
    const [reviewConfig, setReviewConfig] = React.useState(initConfig);
    // Purchase options for the catalog items these rows matched, so the
    // "how it will land" sentence can say "Sugar will have 4 vendors" rather
    // than trailing off. Non-fatal: without it the sentence simply omits the
    // count, and the table renders exactly as before.
    const [optionsByItemId, setOptionsByItemId] = React.useState({});
    const matchedIdKey = items
      .map((i) => i._existingItem?.id)
      .filter((id) => id != null)
      .join(',');
    React.useEffect(() => {
      const ids = [...new Set(matchedIdKey.split(',').filter(Boolean))];
      if (!storeId || ids.length === 0) {
        setOptionsByItemId({});
        return undefined;
      }
      let cancelled = false;
      fetchOptionsForItems(storeId, ids)
        .then((grouped) => { if (!cancelled) setOptionsByItemId(grouped || {}); })
        .catch((err) => {
          console.warn('[import-approval] Purchase options unavailable (non-fatal):', err?.message);
          if (!cancelled) setOptionsByItemId({});
        });
      return () => { cancelled = true; };
    }, [matchedIdKey]);
    const landingContextFor = React.useCallback((row) => {
      const matchedId = row?._existingItem?.id;
      const options = matchedId != null ? (optionsByItemId[matchedId] || []) : [];
      const active = activeOptions(options);
      return {
        existingVendorCount: distinctVendorCount(active),
        existingOptionCount: active.length,
        countUnit: row?._existingItem?.unit || row?.unit || null,
      };
    }, [optionsByItemId]);
    React.useEffect(() => { liveItems = items; }, [items]);
    React.useEffect(() => { liveVendorCaptures = vendorCaptures; }, [vendorCaptures]);
    React.useEffect(() => { liveFeeCaptures = feeCaptures; }, [feeCaptures]);
    React.useEffect(() => {
      setVendorCapturesRef = (captures) => setVendorCaptures((captures || []).map(seedCaptureChoice));
      if (pendingVendorCaptures) {
        setVendorCapturesRef(pendingVendorCaptures);
        pendingVendorCaptures = null;
      }
      return () => { setVendorCapturesRef = null; };
    }, []);
    React.useEffect(() => {
      // Background analysis re-delivers the full capture list as each file
      // finishes; a wholesale replace would re-check boxes the user already
      // unchecked. Keys are stable ('fees:<file>'), so carry the user's
      // existing choices over the freshly seeded defaults — new plan columns
      // still default to checked via seedFeeChoices.
      setFeeCapturesRef = (captures) => setFeeCaptures((prev) => {
        const prevChoices = new Map((prev || []).map((c) => [c.key, c.choices || {}]));
        return (captures || []).map((c) => {
          const seeded = seedFeeChoices(c);
          const kept = prevChoices.get(c.key);
          return kept ? { ...seeded, choices: { ...(seeded.choices || {}), ...kept } } : seeded;
        });
      });
      if (pendingFeeCaptures) {
        setFeeCapturesRef(pendingFeeCaptures);
        pendingFeeCaptures = null;
      }
      return () => { setFeeCapturesRef = null; };
    }, []);
    React.useEffect(() => {
      // Accepts a functional updater (prev => next), like React's setState.
      setItemsRef = (updater) => setItems(updater);
      if (pendingSetItems) {
        setItems(pendingSetItems);
        pendingSetItems = null;
      }
      return () => { setItemsRef = null; };
    }, []);
    React.useEffect(() => {
      el._setReviewConfig = (next) => setReviewConfig((prev) => ({ ...prev, ...(next || {}) }));
      return () => { delete el._setReviewConfig; };
    }, []);
    const handleUpdate = React.useCallback((key, field, value) => {
      setItems((prev) => {
        const target = prev.find((i) => (i._review_key ?? i.id) === key);
        if (!target) return prev;
        // Canonical item name is shared by every vendor product under that
        // item; product-specific fields remain isolated by `_review_key`.
        if (field === 'canonical_description') {
          return prev.map((i) => i.id === target.id ? { ...i, [field]: value } : i);
        }
        return prev.map((i) => (i._review_key ?? i.id) === key ? { ...i, [field]: value } : i);
      });
    }, []);
    const handleReclassify = React.useCallback((key, cls) => {
      setItems((prev) => prev.map((i) => (i._review_key ?? i.id) === key ? { ...i, _classificationOverride: cls } : i));
    }, []);
    const handleCaptureChoice = React.useCallback((key, choice) => {
      setVendorCaptures((prev) => prev.map((c) => c.key === key ? { ...c, choice } : c));
    }, []);
    const handleFeeToggle = React.useCallback((key, column, checked) => {
      setFeeCaptures((prev) => prev.map((c) => c.key === key
        ? { ...c, choices: { ...(c.choices || {}), [column]: checked } }
        : c));
    }, []);
    // Two ways to say "don't put this in my catalog": leave it as unchanged,
    // or disapprove it outright. Both send the row down the host's rejected
    // path (which, for the flows where the edge function already inserted the
    // rows, is what deletes them again).
    const actionableCount = items.filter((i) => !isDeclinedImportRow(i)).length;
    const handleImport = React.useCallback(async () => {
      if (!onApprove) return;
      setIsImporting(true);
      const approved = items.filter((i) => !isDeclinedImportRow(i));
      const rejected = items.filter(isDeclinedImportRow);
      // Third argument: extras from the modal's lower portion (vendor capture
      // choices, fee-capture accept choices). Existing two-argument callers
      // are unaffected; `feeCaptures` is additive for the same reason.
      const extras = {
        vendorCaptures: vendorCaptures.map((c) => ({ ...c })),
        feeCaptures: feeCaptures.map((c) => ({ ...c, choices: { ...(c.choices || {}) } })),
        // null = the reviewer never touched the relationships section, so the
        // host's default plan (accept everything proposed) stands unchanged.
        relationshipDecisions: liveRelationshipDecisions
          ? {
              uncheckedParentIds: [...(liveRelationshipDecisions.uncheckedParentIds || [])],
              uncheckedOptionIds: [...(liveRelationshipDecisions.uncheckedOptionIds || [])],
              uncheckedSuggestionIds: [...(liveRelationshipDecisions.uncheckedSuggestionIds || [])],
            }
          : null,
      };
      try { await onApprove(approved, rejected, extras); } finally { setIsImporting(false); }
    }, [items, vendorCaptures, feeCaptures]);
    return React.createElement(ImportApprovalTable, {
      items,
      onUpdateItem: handleUpdate,
      onReclassifyItem: handleReclassify,
      onImport: handleImport,
      onBack: onCancel,
      isImporting,
      hidePriceColumn: !!reviewConfig.hidePriceColumn,
      storeId,
      columns: reviewConfig.columns,
      rowNoun: reviewConfig.rowNoun,
      allowVendorProduct: reviewConfig.allowVendorProduct,
      helpPage,
      landingContextFor,
      relationships: reviewConfig.relationships && reviewConfig.relationships.length > 0
        ? { groups: reviewConfig.relationships }
        : null,
      importSummary: reviewConfig.importSummary || null,
      onRelationshipsChange: (decisions) => { liveRelationshipDecisions = decisions; },
      footerExtra: (vendorCaptures.length > 0 || feeCaptures.length > 0)
        ? React.createElement(React.Fragment, null,
            vendorCaptures.length > 0
              ? React.createElement(VendorCaptureCard, {
                  captures: vendorCaptures,
                  onChangeChoice: handleCaptureChoice,
                })
              : null,
            feeCaptures.length > 0
              ? React.createElement(FeeAssumptionsCard, {
                  captures: feeCaptures,
                  onToggleFee: handleFeeToggle,
                })
              : null,
          )
        : null,
      // No host label → the table's own "Import N items" default. The table
      // renders its own "Saving…" state while a save is in flight.
      importButtonLabel: reviewConfig.importButtonLabel
        ? reviewConfig.importButtonLabel.replace('{count}', String(actionableCount))
        : undefined,
    });
  }
  const root = window.ReactDOM.createRoot(el);
  root.render(React.createElement(ImportApprovalMount, {
    initItems: taggedItems,
    initCaptures: liveVendorCaptures,
    initFeeCaptures: liveFeeCaptures,
    initConfig: {
      hidePriceColumn: !!hidePriceColumn,
      columns,
      rowNoun,
      allowVendorProduct,
      importButtonLabel,
      relationships: Array.isArray(initialRelationshipGroups) ? initialRelationshipGroups : [],
      importSummary: initialImportSummary,
    },
  }));
  el._importApprovalRoot = root;
  el._getCurrentItems = () => liveItems.map((i) => ({ ...i }));
  // Late-arriving document analysis (vendor contact found on an uploaded
  // invoice) updates the already-open review modal through this setter.
  el._setVendorCaptures = (captures) => {
    if (typeof setVendorCapturesRef === 'function') {
      setVendorCapturesRef(captures);
    } else {
      pendingVendorCaptures = captures;
      liveVendorCaptures = (captures || []).map(seedCaptureChoice);
    }
  };
  el._getVendorCaptures = () => liveVendorCaptures.map((c) => ({ ...c }));
  // Late-arriving document analysis (additional charges / vendor fee plan
  // found on an uploaded invoice) updates the already-open review modal
  // through this setter — the exact mirror of _setVendorCaptures above.
  el._setFeeCaptures = (captures) => {
    if (typeof setFeeCapturesRef === 'function') {
      setFeeCapturesRef(captures);
    } else {
      pendingFeeCaptures = captures;
      liveFeeCaptures = (captures || []).map(seedFeeChoices);
    }
  };
  el._getFeeCaptures = () => liveFeeCaptures.map((c) => ({ ...c, choices: { ...(c.choices || {}) } }));
  // Replace the whole item set live (no remount). Every row is tagged 'new' so it
  // counts toward the approve/reject split unless the user marks it unchanged.
  el._setItems = (next) => {
    const tagged = (next || []).map(tagNew);
    liveItems = tagged;
    if (typeof setItemsRef === 'function') setItemsRef(() => tagged);
    else pendingSetItems = tagged;
  };
  // Append rows live, de-duplicated by their review identity. Source-ledger
  // imports may legitimately contain several vendor products under one item.
  el._appendItems = (rows) => {
    const add = (rows || []).map(tagNew);
    if (!add.length) return;
    const merge = (prev) => {
      const seen = new Set((prev || []).map((p) => String(p._review_key ?? p.id)));
      const fresh = add.filter((r) => r.id == null || !seen.has(String(r._review_key ?? r.id)));
      return [...(prev || []), ...fresh];
    };
    if (typeof setItemsRef === 'function') setItemsRef(merge);
    else pendingSetItems = merge(pendingSetItems || liveItems);
  };
  return true;
};

// Make Item Card Library available globally
window.ItemCard = ItemCard
window.ItemCardContainer = ItemCardContainer
window.ItemCardDemo = ItemCardDemo
window.DatabaseBrowser = DatabaseBrowser
window.DatabaseColumn = DatabaseColumn

// Configure Rails CSRF token for axios
const token = document.querySelector('meta[name="csrf-token"]');
if (token) {
  axios.defaults.headers.common['X-CSRF-Token'] = token.getAttribute('content');
}

// VendorEditor component will mount normally on vendor-editor.html

// Wait for DOM to be fully loaded
document.addEventListener('DOMContentLoaded', () => {
  console.log('🚀 DOM loaded, initializing React components... [BUILD: 20260413c]');

  try {
    normalizeSidebarMenu();
    // Initialize React App if container exists
    const reactAppContainer = document.getElementById('react-app-root');
    console.log('🔍 React app container:', reactAppContainer);

    /*
    if (reactAppContainer) {
      console.log('🔍 Found React container, checking App component...');
      console.log('🔍 App component:', App);
      
      try {
        const { createRoot } = ReactDOM;
        const root = createRoot(reactAppContainer);
        root.render(React.createElement(App));
        
        // Also expose some global functions for the app
        window.showNotification = (message, type = 'info') => {
          console.log(`Notification (${type}): ${message}`);
          // You can implement actual notification logic here
        };
        
        console.log('✅ React App rendered successfully');
      } catch (error) {
        console.error('❌ Error rendering React App:', error);
        reactAppContainer.innerHTML = '<div class="alert alert-danger">Error loading application. Check console for details.</div>';
      }
    } else {
      console.log('ℹ️ No React app container found');
    }
    */

    // Initialize ModernLayout if container exists
    /*
    const modernLayoutContainer = document.getElementById('modern-layout-root');
    if (modernLayoutContainer) {
      try {
        const currentPath = window.location.pathname;
        
        const { createRoot } = ReactDOM;
        const root = createRoot(modernLayoutContainer);
        root.render(React.createElement(ModernLayout, {
          currentPath: currentPath
        }));
        
        console.log('ModernLayout component rendered successfully');
      } catch (error) {
        console.error('Error rendering ModernLayout:', error);
      }
    }
    */

    const controlPanelElement = document.getElementById('control-panel-root')
    if (controlPanelElement) {
      const root = createRoot(controlPanelElement)
      root.render(React.createElement(ControlPanel))
    }

    // Initialize Inventory Manager if container exists
    const inventoryContainer = document.getElementById('inventory-manager');
    if (inventoryContainer) {
      console.log('✅ Found inventory-manager container, initializing React component...');
      try {
        const root = createRoot(inventoryContainer);
        root.render(React.createElement(InventoryManager));
        console.log('✅ InventoryManager component rendered successfully');
      } catch (error) {
        console.error('❌ Error rendering InventoryManager:', error);
        // Fallback: show a simple message
        inventoryContainer.innerHTML = '<div class="alert alert-warning">Loading inventory management system...</div>';
      }
    } else {
      console.log('ℹ️ inventory-manager container not found (this is normal for non-inventory pages)');
    }

    // Global Counter Intelligence drawer — available on every page that loads
    // the bundle. Opened by the corner FAB launcher (and any page that
    // dispatches the `counter-intelligence:open` event).
    try {
      let ciRoot = document.getElementById('counter-intelligence-root');
      if (!ciRoot) {
        ciRoot = document.createElement('div');
        ciRoot.id = 'counter-intelligence-root';
        document.body.appendChild(ciRoot);
      }
      if (!ciRoot.dataset.mounted) {
        ciRoot.dataset.mounted = '1';
        createRoot(ciRoot).render(React.createElement(CounterIntelligenceDrawer));
        console.log('✅ Counter Intelligence drawer mounted');
      }
    } catch (error) {
      console.error('❌ Error mounting Counter Intelligence drawer:', error);
    }

    // Initialize Count Session Manager if container exists
    const countSessionContainer = document.getElementById('count-session-manager');
    if (countSessionContainer) {
      console.log('✅ Found count-session-manager container, initializing React component...');
      try {
        // Get initialStep from data attribute if present
        const initialStep = countSessionContainer.dataset.initialStep || 'create-session';
        console.log('🎯 CountSessionManager initialStep:', initialStep);
        
        const root = createRoot(countSessionContainer);
        root.render(React.createElement(CountSessionManager, {
          initialStep: initialStep,
          onCancel: () => {
            console.log('Count session cancelled');
          }
        }));
        console.log('✅ CountSessionManager component rendered successfully');
      } catch (error) {
        console.error('❌ Error rendering CountSessionManager:', error);
        countSessionContainer.innerHTML = '<div class="alert alert-warning">Loading count session management system...</div>';
      }
    } else {
      console.log('ℹ️ count-session-manager container not found (this is normal for non-count pages)');
    }

    // Initialize Reports if container exists
    const reportsContainer = document.getElementById('reports-manager');
    if (reportsContainer) {
      console.log('✅ Found reports-manager container, initializing React component...');
      try {
        const root = createRoot(reportsContainer);
        root.render(React.createElement(Reports));
        console.log('✅ Reports component rendered successfully');
      } catch (error) {
        console.error('❌ Error rendering Reports:', error);
        reportsContainer.innerHTML = '<div class="alert alert-warning">Loading reports system...</div>';
      }
    } else {
      console.log('ℹ️ reports-manager container not found (this is normal for non-reports pages)');
    }

    // Initialize Orders Page if container exists
    const ordersPageContainer = document.getElementById('orders-page-mount');
    if (ordersPageContainer) {
      console.log('✅ Found orders-page-mount container, initializing React component...');
      try {
        let storeId = null;
        let storeName = '';
        let storeAddress = '';
        let userEmail = '';
        let userName = '';
        try {
          const sidRaw = localStorage.getItem('selectedStoreId');
          if (sidRaw) {
            const parsed = Number(sidRaw);
            storeId = Number.isFinite(parsed) ? parsed : sidRaw;
          }
          const stoRaw = localStorage.getItem('currentStore');
          if (stoRaw) {
            try {
              const parsed = JSON.parse(stoRaw);
              storeName = parsed?.name || parsed?.store_name || '';
              storeAddress = parsed?.address || '';
              if (!storeId && parsed?.id) {
                const sid = Number(parsed.id);
                storeId = Number.isFinite(sid) ? sid : parsed.id;
              }
            } catch {
              storeName = stoRaw;
            }
          }
          // Match the sidebar's "Hello, X" + "User" rows: prefer the live
          // sidebar values, fall back to localStorage / Supabase auth metadata.
          const sbEmail = document.getElementById('current-user-email');
          if (sbEmail) {
            userEmail = (sbEmail.getAttribute('title') || sbEmail.textContent || '').trim();
          }
          const sbStore = document.getElementById('current-store-name');
          if (sbStore && !storeName) {
            storeName = (sbStore.getAttribute('title') || sbStore.textContent || '').trim();
          }
          const sbGreet = document.getElementById('sidebar-greeting');
          if (sbGreet) {
            const greet = (sbGreet.textContent || '').trim();
            const m = greet.match(/^Hello,\s*(.+)$/i);
            if (m && m[1]) userName = m[1].trim();
          }
          if (!userEmail) {
            const ueRaw = localStorage.getItem('userEmail') || localStorage.getItem('current_user_email');
            if (ueRaw) userEmail = String(ueRaw).trim();
          }
          if (!userName) {
            const unRaw = localStorage.getItem('userName') || localStorage.getItem('user_display_name');
            if (unRaw) userName = String(unRaw).trim();
          }
        } catch (err) {
          console.warn('Failed to read orders page localStorage:', err);
        }
        const root = createRoot(ordersPageContainer);
        root.render(React.createElement(OrdersPage, { storeId, storeName, storeAddress, userEmail, userName }));
        console.log('✅ OrdersPage component rendered successfully');
      } catch (error) {
        console.error('❌ Error rendering OrdersPage:', error);
        ordersPageContainer.innerHTML = '<div class="alert alert-warning">Loading orders system...</div>';
      }
    } else {
      console.log('ℹ️ orders-page-mount container not found (this is normal for non-orders pages)');
    }

    // Initialize Ordering Hub if container exists
    const orderingHubContainer = document.getElementById('ordering-hub-mount');
    if (orderingHubContainer) {
      console.log('✅ Found ordering-hub-mount container, initializing React component...');
      try {
        let storeId = null;
        let storeName = '';
        let storeAddress = '';
        let userEmail = '';
        let userName = '';
        try {
          const sidRaw = localStorage.getItem('selectedStoreId');
          if (sidRaw) {
            const parsed = Number(sidRaw);
            storeId = Number.isFinite(parsed) ? parsed : sidRaw;
          }
          const stoRaw = localStorage.getItem('currentStore');
          if (stoRaw) {
            try {
              const parsed = JSON.parse(stoRaw);
              storeName = parsed?.name || parsed?.store_name || '';
              storeAddress = parsed?.address || '';
              if (!storeId && parsed?.id) {
                const sid = Number(parsed.id);
                storeId = Number.isFinite(sid) ? sid : parsed.id;
              }
            } catch { storeName = stoRaw; }
          }
          const sbEmail = document.getElementById('current-user-email');
          if (sbEmail) userEmail = (sbEmail.getAttribute('title') || sbEmail.textContent || '').trim();
          const sbStore = document.getElementById('current-store-name');
          if (sbStore && !storeName) storeName = (sbStore.getAttribute('title') || sbStore.textContent || '').trim();
          const sbGreet = document.getElementById('sidebar-greeting');
          if (sbGreet) {
            const greet = (sbGreet.textContent || '').trim();
            const m = greet.match(/^Hello,\s*(.+)$/i);
            if (m && m[1]) userName = m[1].trim();
          }
          if (!userEmail) {
            const ueRaw = localStorage.getItem('userEmail') || localStorage.getItem('current_user_email');
            if (ueRaw) userEmail = String(ueRaw).trim();
          }
          if (!userName) {
            const unRaw = localStorage.getItem('userName') || localStorage.getItem('user_display_name');
            if (unRaw) userName = String(unRaw).trim();
          }
        } catch (err) {
          console.warn('Failed to read ordering hub localStorage:', err);
        }
        const root = createRoot(orderingHubContainer);
        root.render(React.createElement(OrderingHub, { storeId, storeName, storeAddress, userEmail, userName }));
        console.log('✅ OrderingHub component rendered successfully');
      } catch (error) {
        console.error('❌ Error rendering OrderingHub:', error);
        orderingHubContainer.innerHTML = '<div class="alert alert-warning">Loading ordering hub...</div>';
      }
    } else {
      console.log('ℹ️ ordering-hub-mount container not found (this is normal for non-ordering pages)');
    }

    // Initialize Vendor Editor if container exists
    const vendorEditorContainer = document.getElementById('vendor-editor-manager');
    if (vendorEditorContainer) {
      console.log('✅ Found vendor-editor-manager container, initializing React component...');
      try {
        const root = createRoot(vendorEditorContainer);
        root.render(React.createElement(VendorEditor));
        console.log('✅ VendorEditor component rendered successfully');
      } catch (error) {
        console.error('❌ Error rendering VendorEditor:', error);
        vendorEditorContainer.innerHTML = '<div class="alert alert-warning">Loading vendor editor system...</div>';
      }
    } else {
      console.log('ℹ️ vendor-editor-manager container not found (this is normal for non-vendor pages)');
    }

    // Initialize Sessions Calendar Only if container exists
    const sessionsCalendarContainer = document.getElementById('sessions-calendar-only');
    if (sessionsCalendarContainer) {
      console.log('✅ Found sessions-calendar-only container, initializing React component...');
      try {
        const root = createRoot(sessionsCalendarContainer);
        root.render(React.createElement(SessionsCalendar, {
          onSessionSelect: (session) => {
            console.log('Selected session from calendar:', session);
            // Navigate to count interface for active sessions
            if (session.status === 'draft' || session.status === 'in_progress') {
              window.location.href = 'count.html';
            }
          }
        }));
        console.log('✅ SessionsCalendar component rendered successfully');
      } catch (error) {
        console.error('❌ Error rendering SessionsCalendar:', error);
        sessionsCalendarContainer.innerHTML = '<div class="alert alert-warning">Loading calendar...</div>';
      }
    } else {
      console.log('ℹ️ sessions-calendar-only container not found (this is normal for non-calendar pages)');
    }

    // Initialize Session Archive Page if container exists
    const sessionArchiveContainer = document.getElementById('session-archive-page');
    if (sessionArchiveContainer) {
      console.log('✅ Found session-archive-page container, initializing React component...');
      try {
        const root = createRoot(sessionArchiveContainer);
        root.render(React.createElement(SessionArchivePage));
        console.log('✅ SessionArchivePage component rendered successfully');
      } catch (error) {
        console.error('❌ Error rendering SessionArchivePage:', error);
        sessionArchiveContainer.innerHTML = '<div class="alert alert-warning">Loading archive...</div>';
      }
    }

    // Initialize the Invoice Inbox (formerly Update Prices) if its container exists
    const updatePricesContainer = document.getElementById('update-prices-manager');
    if (updatePricesContainer) {
      console.log('✅ Found update-prices-manager container, initializing React component...');
      try {
        const root = createRoot(updatePricesContainer);
        root.render(React.createElement(InvoiceInbox));
        console.log('✅ InvoiceInbox component rendered successfully');
      } catch (error) {
        console.error('❌ Error rendering InvoiceInbox:', error);
        updatePricesContainer.innerHTML = '<div class="alert alert-warning">Loading invoice inbox...</div>';
      }
    }

    // Initialize Session Trash Page if container exists
    const sessionTrashContainer = document.getElementById('session-trash-page');
    if (sessionTrashContainer) {
      console.log('✅ Found session-trash-page container, initializing React component...');
      try {
        const root = createRoot(sessionTrashContainer);
        root.render(React.createElement(SessionTrashPage));
        console.log('✅ SessionTrashPage component rendered successfully');
      } catch (error) {
        console.error('❌ Error rendering SessionTrashPage:', error);
        sessionTrashContainer.innerHTML = '<div class="alert alert-warning">Loading trash...</div>';
      }
    }

    // Initialize Count Interface if container exists
    const countInterfaceContainer = document.getElementById('count-interface-container');
    if (countInterfaceContainer) {
      console.log('✅ Found count-interface-container, initializing React component...');
      try {
        const root = createRoot(countInterfaceContainer);

        // Check if we're on the count-session.html or count-mobile.html page.
        // count-mobile.html is the focused, chrome-less variant used by the
        // Expo wrapper; it passes mobileTool=true so CountInterface can render
        // its compact mobile layout (added in subsequent commits).
        const isCountSession = window.location.pathname.includes('count-session.html');
        const isCountMobile = window.location.pathname.includes('count-mobile.html');
        if (isCountSession || isCountMobile) {
          console.log(`🎯 On ${isCountMobile ? 'count-mobile' : 'count-session'} page, rendering CountInterface component with session ID`);
          // Get session ID from URL
          const urlParams = new URLSearchParams(window.location.search);
          const sessionId = urlParams.get('session');

          // Persist active session id for components that read from sessionStorage
          if (sessionId) {
            try {
              sessionStorage.setItem('activeSessionId', sessionId);
            } catch (e) {
              console.warn('⚠️ Unable to write activeSessionId to sessionStorage:', e);
            }
          }

          root.render(React.createElement(CountInterface, {
            sessionId: sessionId,
            startWithSession: true,
            mobileTool: isCountMobile,
            onCancel: () => {
              console.log('Count interface cancelled');
              window.location.href = 'count.html?view=overview';
            }
          }));
          console.log('✅ CountInterface component rendered successfully with session ID:', sessionId);
        } else {
          console.log('🎯 On count page, rendering CountInterface component');
          root.render(React.createElement(CountInterface, {
            onCancel: () => {
              console.log('Count interface cancelled');
              window.location.href = 'count-sessions.html';
            }
          }));
          console.log('✅ CountInterface component rendered successfully');
        }
      } catch (error) {
        console.error('❌ Error rendering count component:', error);
        countInterfaceContainer.innerHTML = '<div class="alert alert-warning">Loading count interface...</div>';
      }
    } else {
      console.log('ℹ️ count-interface-container not found (this is normal for non-count pages)');
    }

    // Initialize Feather icons
    if (typeof feather !== 'undefined') {
      feather.replace();
    }
  } catch (error) {
    console.error('❌ Error initializing React components:', error);
    console.error('❌ Error details:', error.stack);
  }
  
  // Listen for custom template editor mount events
  document.addEventListener('mountTemplateEditor', (event) => {
    console.log('🔍 [TEMPLATE-EDITOR] Received mount request:', event.detail);
    
    const { containerId, initialStep, onCancel } = event.detail;
    const container = document.getElementById(containerId);
    
    if (container) {
      console.log('✅ [TEMPLATE-EDITOR] Mounting CountSessionManager in:', containerId);
      try {
        // Get mode from data attribute
        const mode = container.dataset.mode || 'normal';
        
        const root = createRoot(container);
        root.render(React.createElement(CountSessionManager, {
          initialStep: initialStep || 'dashboard',
          mode: mode,
          onCancel: onCancel || (() => {
            console.log('Template editor cancelled');
          })
        }));
        console.log('✅ [TEMPLATE-EDITOR] CountSessionManager mounted successfully with mode:', mode);
      } catch (error) {
        console.error('❌ [TEMPLATE-EDITOR] Error mounting CountSessionManager:', error);
        container.innerHTML = '<div class="alert alert-warning">Loading template editor...</div>';
      }
    } else {
      console.error('❌ [TEMPLATE-EDITOR] Container not found:', containerId);
    }
  });

  // Keep a persistent root to avoid re-creating on every update
  let countSessionItemEditorRoot = null;

  const publishTemplateEditorItems = (updatedItems) => {
    window.currentTemplateItems = updatedItems;
    window.updateTemplateEditorSummary?.(updatedItems);
    window.syncTemplateMetaEditor?.({ templateItems: updatedItems });
  };

  // Listen for Count Session Item Editor mount events
  document.addEventListener('mountCountSessionItemEditor', (event) => {
    console.log('🔍 [COUNT-EDITOR] Received mount request:', event.detail);

    const { containerId, templateItems, onUpdate } = event.detail;
    const container = document.getElementById(containerId);

    if (container) {
      console.log('✅ [COUNT-EDITOR] Mounting CountSessionItemEditor in:', containerId);
      console.log('✅ [COUNT-EDITOR] Template items:', templateItems);
      try {
        if (!countSessionItemEditorRoot) {
          container.innerHTML = '';
          countSessionItemEditorRoot = createRoot(container);
        }
        const renderItemLocationFolders = () => {
          // Always use the most current template items from global state
          const currentTemplateItems = window.currentTemplateItems || templateItems || {};
          console.log('🔄 [ORDER-TOGGLE] Rendering with items:', Object.keys(currentTemplateItems).length, 'locations');

          countSessionItemEditorRoot.render(React.createElement(ItemLocationFolders, {
            templateItems: currentTemplateItems,
            sessionId: window.currentSessionId || null,
            orderCalculationsEnabled: window.selectedOrderCalculationsEnabled || false,
            onUpdate: onUpdate || ((updatedItems) => {
              console.log('Items updated:', updatedItems);
              publishTemplateEditorItems(updatedItems);

              // Dispatch update event to trigger re-render
              const event = new CustomEvent('updateCountSessionItemEditor', {
                detail: {
                  templateItems: updatedItems
                }
              });
              document.dispatchEvent(event);
              console.log('🔄 [DRAG-DROP] Dispatched update event after drag operation');
            })
          }));
        };

        // Initial render
        renderItemLocationFolders();

        // Listen for Order with Session toggle changes
        const handleOrderToggleChange = () => {
          console.log('🔄 [ORDER-WITH-SESSION] Re-rendering ItemLocationFolders with new state:', window.selectedOrderCalculationsEnabled);
          renderItemLocationFolders();
        };

        // Add event listener for toggle changes (remove existing first to avoid duplicates)
        if (window.cleanupOrderToggleListener) {
          window.cleanupOrderToggleListener();
        }
        document.addEventListener('orderWithSessionToggleChanged', handleOrderToggleChange);
        console.log('✅ [ORDER-TOGGLE] Event listener added for toggle changes');

        // Store cleanup function for potential future use
        window.cleanupOrderToggleListener = () => {
          document.removeEventListener('orderWithSessionToggleChanged', handleOrderToggleChange);
          console.log('🧹 [ORDER-TOGGLE] Event listener removed');
        };
        console.log('✅ [COUNT-EDITOR] CountSessionItemEditor mounted successfully');
      } catch (error) {
        console.error('❌ [COUNT-EDITOR] Error mounting CountSessionItemEditor:', error);
        container.innerHTML = '<div class="alert alert-warning">Loading item editor...</div>';
      }
    } else {
      console.error('❌ [COUNT-EDITOR] Container not found:', containerId);
    }
  });

  // Listen for Count Session Item Editor update events
  document.addEventListener('updateCountSessionItemEditor', (event) => {
    console.log('🔍 [COUNT-EDITOR] Received update request:', event.detail);

    const { templateItems } = event.detail;
    const container = document.getElementById('count-session-item-editor');

    if (container) {
      console.log('✅ [COUNT-EDITOR] Updating CountSessionItemEditor');
      console.log('✅ [COUNT-EDITOR] Template items:', templateItems);
      console.log('🔄 [COUNT-EDITOR] Order calculations enabled:', window.selectedOrderCalculationsEnabled);
      try {
        if (!countSessionItemEditorRoot) {
          container.innerHTML = '';
          countSessionItemEditorRoot = createRoot(container);
        }
        // Always use the most current template items from global state
        const currentTemplateItems = window.currentTemplateItems || templateItems || {};
        console.log('🔄 [COUNT-EDITOR] Rendering with items:', Object.keys(currentTemplateItems).length, 'locations');

        countSessionItemEditorRoot.render(React.createElement(ItemLocationFolders, {
          templateItems: currentTemplateItems,
          sessionId: window.currentSessionId || null,
          orderCalculationsEnabled: window.selectedOrderCalculationsEnabled || false,
          onUpdate: (updatedItems) => {
            console.log('Items updated:', updatedItems);
            publishTemplateEditorItems(updatedItems);

            // Dispatch update event to trigger re-render
            const event = new CustomEvent('updateCountSessionItemEditor', {
              detail: {
                templateItems: updatedItems
              }
            });
            document.dispatchEvent(event);
            console.log('🔄 [DRAG-DROP] Dispatched update event after drag operation');
          }
        }));

        // Add event listener for toggle changes (if not already added)
        if (!window.orderToggleListenerAdded) {
          const handleOrderToggleChange = () => {
            console.log('🔄 [ORDER-WITH-SESSION] Re-rendering ItemLocationFolders with new state:', window.selectedOrderCalculationsEnabled);
            // Re-render with current template items and new toggle state
            const currentTemplateItems = window.currentTemplateItems || {};
            countSessionItemEditorRoot.render(React.createElement(ItemLocationFolders, {
              templateItems: currentTemplateItems,
              sessionId: window.currentSessionId || null,
              orderCalculationsEnabled: window.selectedOrderCalculationsEnabled || false,
              onUpdate: (updatedItems) => {
                console.log('Items updated:', updatedItems);
                publishTemplateEditorItems(updatedItems);
                const event = new CustomEvent('updateCountSessionItemEditor', {
                  detail: { templateItems: updatedItems }
                });
                document.dispatchEvent(event);
              }
            }));
          };

          document.addEventListener('orderWithSessionToggleChanged', handleOrderToggleChange);
          window.orderToggleListenerAdded = true;
          console.log('✅ [ORDER-TOGGLE] Event listener added for updateCountSessionItemEditor');
        }

        console.log('✅ [COUNT-EDITOR] CountSessionItemEditor updated successfully');
      } catch (error) {
        console.error('❌ [COUNT-EDITOR] Error updating CountSessionItemEditor:', error);
      }
    }
  });

  // Add event listener to refresh count interface when new sessions are created
  document.addEventListener('refreshCountInterface', () => {
    console.log('🔄 [COUNT-INTERFACE] Received refresh request');

    if (typeof countInterfaceRoot !== 'undefined' && countInterfaceRoot) {
      try {
        // Re-render the CountInterface to reload sessions
        countInterfaceRoot.render(React.createElement(CountInterface, {
          onCancel: () => {
            console.log('Count interface cancelled');
          }
        }));
        console.log('✅ [COUNT-INTERFACE] CountInterface refreshed successfully');
      } catch (error) {
        console.error('❌ [COUNT-INTERFACE] Error refreshing CountInterface:', error);
      }
    }
  });

  // Listen for Item Editor mount events
  document.addEventListener('mountItemEditor', (event) => {
    console.log('🔍 [ITEM-EDITOR] Received mount request:', event.detail);

    const { containerId, selectedItem, onUpdate } = event.detail;
    const container = document.getElementById(containerId);

    if (container) {
      console.log('✅ [ITEM-EDITOR] Mounting ItemEditor in:', containerId);
      try {
        const root = createRoot(container);
        root.render(React.createElement(ItemEditor, {
          selectedItem: selectedItem || null,
          onUpdate: onUpdate || ((itemId, updates) => {
            console.log('Item updated:', itemId, updates);
          })
        }));
        console.log('✅ [ITEM-EDITOR] ItemEditor mounted successfully');
      } catch (error) {
        console.error('❌ [ITEM-EDITOR] Error mounting ItemEditor:', error);
        container.innerHTML = '<div class="alert alert-warning">Loading item editor...</div>';
      }
    } else {
      console.error('❌ [ITEM-EDITOR] Container not found:', containerId);
    }
  });

  // Listen for Item Editor update events
  document.addEventListener('updateItemEditor', (event) => {
    console.log('🔍 [ITEM-EDITOR] Received update request:', event.detail);

    const { selectedItem } = event.detail;
    const container = document.getElementById('item-editor-controls');

    if (container) {
      console.log('✅ [ITEM-EDITOR] Updating ItemEditor');
      try {
        const root = createRoot(container);
        root.render(React.createElement(ItemEditor, {
          selectedItem: selectedItem || null,
          onUpdate: (itemId, updates) => {
            console.log('Item updated:', itemId, updates);
            // Update the item in the global template items
            if (window.currentTemplateItems) {
              Object.keys(window.currentTemplateItems).forEach(templateName => {
                window.currentTemplateItems[templateName] = window.currentTemplateItems[templateName].map(item =>
                  item.id === itemId ? { ...item, ...updates } : item
                );
              });
              // Trigger update of the main item list
              window.updateCountSessionItemEditor && window.updateCountSessionItemEditor();
            }
          }
        }));
        console.log('✅ [ITEM-EDITOR] ItemEditor updated successfully');
      } catch (error) {
        console.error('❌ [ITEM-EDITOR] Error updating ItemEditor:', error);
      }
    }
  });

  // Listen for Item Database Browser mount events
  document.addEventListener('mountItemDatabaseBrowser', (event) => {
    console.log('🔍 [ITEM-DB-BROWSER] Received mount request:', event.detail);

    const { containerId, currentItems, onAddItem } = event.detail;
    const container = document.getElementById(containerId);

    if (container) {
      console.log('✅ [ITEM-DB-BROWSER] Mounting ItemDatabaseBrowser in:', containerId);
      console.log('✅ [ITEM-DB-BROWSER] Current items count:', (currentItems || []).length);
      console.log('✅ [ITEM-DB-BROWSER] onAddItem callback type:', typeof onAddItem);

      try {
        // Clear container first to prevent multiple mounts
        container.innerHTML = '';

        // Store the root globally to prevent recreating
        itemDatabaseBrowserRoot = createRoot(container);

        console.log('🔍 [ITEM-DB-BROWSER] Mounting with inventory data:', window.globalInventoryData?.length || 0, 'items');

        // Populate filter dropdowns with inventory data
        if (window.globalInventoryData && window.globalInventoryData.length > 0 && typeof window.populateFilterDropdowns === 'function') {
          console.log('🔧 [ITEM-DB-BROWSER] Populating filter dropdowns...');
          window.populateFilterDropdowns(window.globalInventoryData);
        }

        itemDatabaseBrowserRoot.render(React.createElement(ItemDatabaseBrowser, {
          currentItems: currentItems || [],
          inventoryData: window.globalInventoryData || [], // Pass global inventory data
          onAddItem: onAddItem || ((item) => {
            console.log('🔧 [ITEM-DB-BROWSER] Default callback - Item added from database browser:', item);
            // Try to call the global function as fallback
            if (typeof window.addItemToCountList === 'function') {
              window.addItemToCountList(item);
            } else {
              console.error('❌ [ITEM-DB-BROWSER] No addItemToCountList function available');
            }
          })
        }));
        console.log('✅ [ITEM-DB-BROWSER] ItemDatabaseBrowser mounted successfully');

        // Replay any cached inventory update that arrived before mount
        if (cachedInventoryUpdate) {
          console.log('🔄 [ITEM-DB-BROWSER] Replaying cached inventory update:', cachedInventoryUpdate.inventoryData?.length || 0, 'items');
          try {
            itemDatabaseBrowserRoot.render(React.createElement(ItemDatabaseBrowser, {
              currentItems: cachedInventoryUpdate.currentItems || [],
              inventoryData: cachedInventoryUpdate.inventoryData || [],
              onAddItem: onAddItem || ((item) => {
                if (typeof window.addItemToCountList === 'function') {
                  window.addItemToCountList(item);
                }
              })
            }));
            console.log('✅ [ITEM-DB-BROWSER] Cached inventory update replayed successfully');
          } catch (replayError) {
            console.error('❌ [ITEM-DB-BROWSER] Error replaying cached inventory update:', replayError);
          }
          cachedInventoryUpdate = null;
        }
      } catch (error) {
        console.error('❌ [ITEM-DB-BROWSER] Error mounting ItemDatabaseBrowser:', error);
        console.error('❌ [ITEM-DB-BROWSER] Error stack:', error.stack);
        container.innerHTML = '<div class="alert alert-warning">Loading item database browser...</div>';
        itemDatabaseBrowserRoot = null;
      }
    } else {
      console.error('❌ [ITEM-DB-BROWSER] Container not found:', containerId);
    }
  });

  // Global variable to store the database browser root to prevent recreating
  let itemDatabaseBrowserRoot = null;

  // Cache for inventory updates that arrive before the browser component mounts
  let cachedInventoryUpdate = null;

  // Listen for inventory data updates from CountSessionManager
  document.addEventListener('updateItemDatabaseBrowserInventory', (event) => {
    console.log('🔄 [ITEM-DB-BROWSER] Received inventory data update:', event.detail.inventoryData?.length || 0, 'items');

    const container = document.getElementById('item-database-browser');
    if (container && itemDatabaseBrowserRoot) {
      console.log('✅ [ITEM-DB-BROWSER] Updating ItemDatabaseBrowser with new inventory data');
      try {
        // Update with new inventory data
        itemDatabaseBrowserRoot.render(React.createElement(ItemDatabaseBrowser, {
          currentItems: event.detail.currentItems || [],
          inventoryData: event.detail.inventoryData || [],
          onAddItem: (item) => {
            console.log('Item added from database browser:', item);
            if (typeof window.addItemToCountList === 'function') {
              window.addItemToCountList(item);
            }
          }
        }));
        console.log('✅ [ITEM-DB-BROWSER] ItemDatabaseBrowser updated with inventory data successfully');
      } catch (error) {
        console.error('❌ [ITEM-DB-BROWSER] Error updating ItemDatabaseBrowser with inventory data:', error);
      }
    } else {
      // Cache the update so it can be replayed when the browser mounts later
      cachedInventoryUpdate = event.detail;
      console.log('📦 [ITEM-DB-BROWSER] Inventory update cached until browser container/root is available');
    }
  });

  // Listen for Item Database Browser update events
  document.addEventListener('updateItemDatabaseBrowser', (event) => {
    console.log('🔍 [ITEM-DB-BROWSER] Received update request:', event.detail);

    const { currentItems } = event.detail;
    const container = document.getElementById('item-database-browser');

    if (container && itemDatabaseBrowserRoot) {
      console.log('✅ [ITEM-DB-BROWSER] Updating existing ItemDatabaseBrowser');
      try {
        // Reuse existing root instead of creating new one
        console.log('🔄 [ITEM-DB-BROWSER] Updating with inventory data:', window.globalInventoryData?.length || 0, 'items');

        itemDatabaseBrowserRoot.render(React.createElement(ItemDatabaseBrowser, {
          currentItems: currentItems || [],
          inventoryData: window.globalInventoryData || [], // Pass global inventory data
          onAddItem: (item) => {
            console.log('Item added from database browser:', item);
            // Trigger the global add item function
            if (typeof window.addItemToCountList === 'function') {
              window.addItemToCountList(item);
            }
          }
        }));
        console.log('✅ [ITEM-DB-BROWSER] ItemDatabaseBrowser updated successfully');
      } catch (error) {
        console.error('❌ [ITEM-DB-BROWSER] Error updating ItemDatabaseBrowser:', error);
      }
    } else {
      console.log('⚠️ [ITEM-DB-BROWSER] No container or root found for update, skipping');
    }
  });

  // Count session creation is now handled directly in count.html
})

// Sidebar links are now fully managed by sidebar-include.js — no manipulation needed
function normalizeSidebarMenu() {}
